import { Alert, Autocomplete, Box, InputAdornment, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { CURATED_ICON_NAMES, DynamicMuiIcon } from '../../shared/components/DynamicMuiIcon';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { AiAssistantRole, AiAssistantRoleStatus, DocumentCategory } from '../../core/types/data-models';

const DEFAULT_COLOR = '#6366F1';

interface AiAssistantRoleDialogProps {
  open: boolean;
  initial?: AiAssistantRole | null;
  existingRoles: AiAssistantRole[];
  onClose: () => void;
  onSaved?: (role: AiAssistantRole) => void;
}

export function AiAssistantRoleDialog({ open, initial, existingRoles, onClose, onSaved }: AiAssistantRoleDialogProps) {
  const qc = useQueryClient();
  const { data: catEnv } = useQuery({
    queryKey: ['documentCategories'],
    queryFn: () => resources.documentCategories.list({ page: 1, pageSize: 200, sort: 'order' }),
  });
  const categories: DocumentCategory[] = (catEnv?.data ?? []).slice().sort(
    (a, b) => (a.order ?? Infinity) - (b.order ?? Infinity),
  );

  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [avatarIcon, setAvatarIcon] = useState('');
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [documentCategoryType, setDocumentCategoryType] = useState('');
  const [order, setOrder] = useState<string>('0');
  const [status, setStatus] = useState<AiAssistantRoleStatus>('Active');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setKey(initial?.key ?? '');
      setName(initial?.name ?? '');
      setDescription(initial?.description ?? '');
      setAvatarIcon(initial?.avatarIcon ?? '');
      setColor(initial?.color ?? DEFAULT_COLOR);
      setDocumentCategoryType(initial?.documentCategoryType ?? '');
      setOrder(initial?.order != null ? String(initial.order) : '0');
      setStatus(initial?.status ?? 'Active');
      setError(null);
    }
  }, [open, initial]);

  // Strict 1:1 role↔category — a category already claimed by another role is
  // not selectable, except the one this role already owns.
  const takenTypes = new Set(
    existingRoles.filter((r) => r.id !== initial?.id).map((r) => r.documentCategoryType),
  );
  const availableCategories = categories.filter(
    (c) => !takenTypes.has(c.type) || c.type === documentCategoryType,
  );

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        key: key.trim(),
        name: name.trim(),
        description: description.trim(),
        avatarIcon: avatarIcon.trim() || null,
        color: color.trim() || null,
        documentCategoryType,
        order: order !== '' ? Number(order) : null,
        status,
      };
      if (initial) return resources.aiAssistantRoles.update(initial.id, payload);
      return resources.aiAssistantRoles.create(payload);
    },
    onSuccess: (role) => {
      qc.invalidateQueries({ queryKey: ['aiAssistantRoles'] });
      qc.invalidateQueries({ queryKey: ['aiAssistantRole', role.id] });
      onSaved?.(role);
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  return (
    <FormDialog
      open={open}
      title={initial ? 'Edit AI Assistant Role' : 'New AI Assistant Role'}
      onClose={onClose}
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
      loading={save.isPending}
      maxWidth="sm"
      submitLabel={initial ? 'Save' : 'Create'}
    >
      <Box>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Stack spacing={2}>
          <TextField
            label="Key"
            required
            value={key}
            onChange={(e) => setKey(e.target.value)}
            disabled={!!initial}
            helperText={initial ? 'Stable identifier — cannot be changed once created.' : 'Stable identifier, e.g. product-manager. Cannot be changed later.'}
            autoFocus={!initial}
          />
          <TextField label="Name" required value={name} onChange={(e) => setName(e.target.value)} autoFocus={!!initial} />
          <TextField
            label="Description"
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
          />
          <Autocomplete
            freeSolo
            options={CURATED_ICON_NAMES as unknown as string[]}
            value={avatarIcon}
            onInputChange={(_e, val) => setAvatarIcon(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Avatar icon"
                helperText="MUI icon component name, e.g. Flag, Insights, TravelExplore"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <DynamicMuiIcon iconName={avatarIcon} fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              component="input"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              sx={{ width: 48, height: 40, p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer' }}
            />
            <TextField
              label="Color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              helperText="Card background color (hex)"
              sx={{ flex: 1 }}
            />
          </Stack>
          <TextField
            select
            label="Document category"
            required
            value={documentCategoryType}
            onChange={(e) => setDocumentCategoryType(e.target.value)}
            helperText="Each category maps to exactly one role."
          >
            {availableCategories.map((c) => (
              <MenuItem key={c.type} value={c.type}>{c.name}</MenuItem>
            ))}
            {documentCategoryType && !availableCategories.some((c) => c.type === documentCategoryType) && (
              <MenuItem value={documentCategoryType}>{documentCategoryType}</MenuItem>
            )}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Order"
              type="number"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              helperText="Lower = first"
              inputProps={{ min: 0 }}
              sx={{ flex: 1 }}
            />
            <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value as AiAssistantRoleStatus)} sx={{ flex: 1 }}>
              {(['Active', 'Inactive'] as const).map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
          </Stack>
          {availableCategories.length === 0 && !documentCategoryType && (
            <Typography variant="caption" color="text.secondary">
              Every document category already has a role assigned.
            </Typography>
          )}
        </Stack>
      </Box>
    </FormDialog>
  );
}
